import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, QrCode, RefreshCw, ShieldAlert, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { ErrorState, Page, PageBody, PageHeader, Panel, Skeleton } from '@/ui/Panel';
import { DataTable, type Column } from '@/ui/DataTable';
import { Button } from '@/ui/Button';
import { Field, Input } from '@/ui/Field';
import { Badge, Code } from '@/ui/Badge';
import { Callout } from '@/ui/Callout';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { api, errorMessage } from '@/lib/api/client';
import { useAtLeast } from '@/lib/auth';
import { countLabel, dateTime, relativeTime, smartDate, time } from '@/lib/format';
import {
  classifyApproveFailure,
  FLOOR_STATUS_META,
  installerCountdown,
  normalizePairingCode,
  PAIRING_CODE_LENGTH,
  pairingExpiresAt,
  readAppUpdate,
  readLockout,
  validatePairingCode,
} from '@/features/system/floorDevices';
import type {
  FloorDevice,
  FloorInstallerLink,
  FloorInstallerMetadata,
  UserSummary,
} from '@/lib/api/types';

/**
 * Android floor handhelds: install, match the pairing code, approve.
 *
 * The one rule this screen exists to enforce: a phone gets access only when a
 * manager types the code the phone is showing and the server agrees it is that
 * phone's. The code is listed in the row (owner decision — it is not a secret,
 * the audit trail is the control), but the dialog still asks for it typed,
 * because the typing is the check. A mismatch keeps the dialog open; anything
 * else the server refuses means the row moved, so the list is re-read.
 *
 * Every write waits for the server. Nothing here is optimistic.
 */

const DEVICES_KEY = ['floor-devices'] as const;
const INSTALLER_KEY = ['floor-installer-metadata'] as const;

export const Route = createFileRoute('/_app/system/floor-devices')({
  staticData: { crumb: 'Floor devices' },
  component: FloorDevicesScreen,
});

function FloorDevicesScreen() {
  const canManage = useAtLeast('manager');
  const queryClient = useQueryClient();
  const [approving, setApproving] = useState<FloorDevice | null>(null);
  const [revoking, setRevoking] = useState<FloorDevice | null>(null);

  // The list route itself is manager-only, so a lower role gets the
  // explanation below instead of a 403 in a toast.
  const devices = useQuery({
    queryKey: DEVICES_KEY,
    queryFn: () => api.get<FloorDevice[]>('/floor/devices'),
    enabled: canManage,
    // Phones enroll themselves; the pending row should appear without a reload.
    refetchInterval: 15_000,
  });

  const installer = useQuery({
    queryKey: INSTALLER_KEY,
    queryFn: () => api.get<FloorInstallerMetadata | null>('/floor/installer-metadata'),
    enabled: canManage,
    staleTime: 5 * 60_000,
  });

  const rows = devices.data ?? [];
  const showApprovedBy = rows.some((r) => r.approvedByUserId !== undefined);
  const showSignIn = rows.some(
    (r) => r.failedLogins !== undefined || r.loginBlockedUntil !== undefined
  );

  // Approvals carry a user id, not a name. One users read resolves them all.
  const staff = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserSummary[]>('/users'),
    staleTime: 5 * 60_000,
    enabled: canManage && showApprovedBy,
  });
  const staffName = new Map<number, string>();
  for (const u of staff.data ?? []) {
    staffName.set(u.id, `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.username);
  }

  const revoke = useMutation({
    mutationFn: (id: number) =>
      api.post<{ id: number; status: string }>(`/floor/devices/${id}/revoke`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DEVICES_KEY });
      toast.success(`${revoking?.name ?? 'Device'} revoked`, {
        description: 'The phone is signed out. To use it again, enroll it in the app.',
      });
      setRevoking(null);
    },
    onError: (error) => {
      void queryClient.invalidateQueries({ queryKey: DEVICES_KEY });
      setRevoking(null);
      toast.error('Could not revoke', { description: errorMessage(error) });
    },
  });

  const pendingCount = rows.filter((r) => r.status === 'pending').length;

  const columns: Array<Column<FloorDevice>> = [
    {
      id: 'name',
      header: 'Device',
      width: 'minmax(0, 1.4fr)',
      cell: (r) => (
        <div className="min-w-0">
          <div className="text-ink truncate text-xs">{r.name}</div>
          <div className="text-2xs text-ink-faint flex items-center gap-1.5">
            <span className="font-mono">#{r.id}</span>
            {r.platform ? <span className="truncate">{r.platform}</span> : null}
          </div>
        </div>
      ),
    },
    {
      id: 'code',
      header: 'Pairing code',
      width: '8rem',
      mono: true,
      cell: (r) => <Code>{r.pairingCode}</Code>,
    },
    {
      id: 'status',
      header: 'Status',
      width: '6.5rem',
      cell: (r) => {
        const meta = FLOOR_STATUS_META[r.status];
        return (
          <span title={meta.detail}>
            <Badge tone={meta.tone} dot={meta.dot}>
              {meta.label}
            </Badge>
          </span>
        );
      },
    },
    {
      id: 'app',
      header: 'App',
      width: '11rem',
      cell: (r) => {
        const reading = readAppUpdate(r, installer.data);
        return (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              {r.appVersion ? (
                <span className="text-ink-muted font-mono text-xs">{r.appVersion}</span>
              ) : (
                <span
                  className="text-ink-faint text-xs"
                  title="The phone reports its build when staff sign in"
                >
                  not reported
                </span>
              )}
              {reading === 'behind' && installer.data ? (
                <Badge
                  tone="warn"
                  className="normal-case"
                >{`Needs update · v${installer.data.version}`}</Badge>
              ) : null}
            </div>
            {r.appBuildSha ? (
              <div className="text-2xs text-ink-faint font-mono" title={r.appBuildSha}>
                {r.appBuildSha.slice(0, 8)}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      id: 'enrolled',
      header: 'Enrolled',
      width: '8rem',
      mono: true,
      hideBelow: 'lg',
      cell: (r) => (
        <div className="min-w-0">
          <span className="text-ink-muted" title={dateTime(r.createdAt)}>
            {smartDate(r.createdAt)}
          </span>
          {r.status === 'pending' ? (
            <div className="text-2xs text-warn" title={dateTime(pairingExpiresAt(r))}>
              code lapses {relativeTime(pairingExpiresAt(r))}
            </div>
          ) : null}
        </div>
      ),
    },
  ];

  if (showApprovedBy) {
    columns.push({
      id: 'approvedBy',
      header: 'Approved by',
      width: '9rem',
      hideBelow: 'xl',
      cell: (r) =>
        r.approvedByUserId ? (
          <span className="text-ink-muted truncate">
            {staffName.get(r.approvedByUserId) ?? `User ${r.approvedByUserId}`}
          </span>
        ) : (
          <span className="text-ink-faint">—</span>
        ),
    });
  }

  if (showSignIn) {
    columns.push({
      id: 'signIn',
      header: 'Sign-in',
      width: '9rem',
      hideBelow: 'xl',
      cell: (r) => {
        const lock = readLockout(r);
        if (lock.state === 'locked') {
          return (
            <Badge tone="negative" className="normal-case">
              Locked until {time(lock.blockedUntil)}
            </Badge>
          );
        }
        if (lock.state === 'failing') {
          return (
            <span className="text-warn text-xs">
              {countLabel(lock.failedLogins, 'failed PIN')}
            </span>
          );
        }
        return <span className="text-ink-faint">—</span>;
      },
    });
  }

  // Without the capability the column is not rendered — the Callout above the
  // list says why, which a row of greyed buttons never would.
  if (canManage) {
    columns.push({
      id: 'actions',
      header: '',
      width: '11rem',
      align: 'right',
      cell: (r) => (
        <div className="flex justify-end gap-1">
          {r.status === 'pending' ? (
            <Button
              size="xs"
              variant="primary"
              icon={<Check className="size-3" />}
              onClick={() => setApproving(r)}
            >
              Approve…
            </Button>
          ) : null}
          {r.status !== 'revoked' ? (
            <Button size="xs" variant="danger" onClick={() => setRevoking(r)}>
              Revoke
            </Button>
          ) : null}
        </div>
      ),
    });
  }

  return (
    <Page>
      <PageHeader
        eyebrow="System"
        title="Floor devices"
        subtitle="Android handhelds for counts, waste and picking. Install the app, match its pairing code here, approve it — staff then sign in with their register PIN."
        actions={
          canManage ? (
            <Button
              size="sm"
              variant="ghost"
              icon={<RefreshCw className="size-3.5" />}
              loading={devices.isFetching && !devices.isLoading}
              onClick={() => void devices.refetch()}
            >
              Refresh
            </Button>
          ) : null
        }
      />

      <PageBody className="space-y-4">
        {!canManage ? (
          <Callout tone="warn" icon={<ShieldAlert />} title="Manager access needed">
            Approving a phone, revoking it and generating the install link are manager
            actions, and the device list itself is manager-only — so nothing loads here on
            your account. Ask a manager to match the code the phone is showing.
          </Callout>
        ) : null}

        <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <Panel
            title="Devices"
            description="Compare the code with the one on the phone before approving. Revoking signs that phone out."
            bleed
            className="min-w-0"
            footer={
              canManage ? (
                <span className="flex items-center gap-3">
                  <span>{countLabel(rows.length, 'device')}</span>
                  {pendingCount > 0 ? (
                    <span className="text-warn">{pendingCount} awaiting approval</span>
                  ) : null}
                  {devices.isFetching ? (
                    <span className="text-signal-ink">refreshing…</span>
                  ) : null}
                </span>
              ) : undefined
            }
          >
            <DataTable
              rows={canManage ? (devices.isLoading ? undefined : rows) : []}
              columns={columns}
              rowKey={(r) => r.id}
              loading={canManage && devices.isLoading}
              error={devices.error}
              onRetry={() => void devices.refetch()}
              keyboard={false}
              rowTone={(r) => (r.status === 'pending' ? 'warn' : null)}
              empty={
                canManage
                  ? {
                      icon: <Smartphone />,
                      title: 'No floor devices enrolled yet',
                      description:
                        'Install the app from the QR on the right. The phone shows up here with a pairing code as soon as it enrolls.',
                    }
                  : {
                      icon: <ShieldAlert />,
                      title: 'Manager access needed',
                      description: 'The device list is only readable by a manager.',
                    }
              }
            />
          </Panel>

          <InstallPanel
            canManage={canManage}
            installer={installer.data}
            loading={canManage && installer.isLoading}
            error={installer.error}
            onRetry={() => void installer.refetch()}
          />
        </div>
      </PageBody>

      <ApproveDialog device={approving} onClose={() => setApproving(null)} />

      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(open) => !open && setRevoking(null)}
        title={`Revoke access for ${revoking?.name ?? ''}?`}
        description="The phone is signed out and its device token is refused from now on."
        confirmLabel="Revoke access"
        loading={revoke.isPending}
        consequences={[
          'Any floor session open on the phone ends immediately',
          'Counts, waste and picks sent from it are rejected',
          'Not reversible from here — to use the phone again, enroll it in the app and approve the new code',
        ]}
        onConfirm={() => revoking && revoke.mutate(revoking.id)}
      />
    </Page>
  );
}

/* ── Approve ──────────────────────────────────────────────────────────────────
   The code is typed, never pre-filled: the whole point is that the operator
   read it off the phone in their hand. The server compares, records a
   mismatch in the audit log, and only then answers — so a wrong code leaves
   the dialog open with the field ready to retype, while any other refusal
   (already approved, expired, revoked) closes it and re-reads the list.    */

function ApproveDialog({
  device,
  onClose,
}: {
  device: FloorDevice | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const inputId = useId();
  const formId = useId();
  const codeInput = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // The dialog stays mounted across open/close so the close animation plays;
  // `shown` keeps the last device for the title while it fades, and every
  // opening starts from a blank field.
  const open = device !== null;
  const [shown, setShown] = useState(device);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (device) {
      setShown(device);
      setCode('');
      setTouched(false);
      setServerError(null);
    }
  }

  const validation = validatePairingCode(code);
  const error = serverError ?? (touched ? validation : null);

  const approve = useMutation({
    mutationFn: (input: { id: number; pairingCode: string }) =>
      api.post<{ id: number; status: string }>(`/floor/devices/${input.id}/approve`, {
        pairingCode: input.pairingCode,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DEVICES_KEY });
      toast.success(`${shown?.name ?? 'Device'} approved`, {
        description: 'Staff can now sign in on it with their register PIN.',
      });
      onClose();
    },
    onError: (err) => {
      const failure = classifyApproveFailure(err);
      if (failure.kind === 'mismatch') {
        // The phone is still pending; the operator just misread. Stay put,
        // and put the caret back in the field — a mouse submit left it on
        // the button — with the wrong code selected so the retype replaces it.
        setServerError(failure.message);
        codeInput.current?.focus();
        codeInput.current?.select();
        return;
      }
      // Anything else means this row is no longer an approvable pending
      // device — someone else acted, or the code lapsed. Re-read and say so.
      void queryClient.invalidateQueries({ queryKey: DEVICES_KEY });
      onClose();
      toast.error('Could not approve', {
        description:
          failure.kind === 'stale'
            ? `${failure.message} The list has been re-read.`
            : failure.message,
      });
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!device) return;
    setTouched(true);
    if (validation) return;
    approve.mutate({ id: device.id, pairingCode: normalizePairingCode(code) });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={`Approve ${shown?.name ?? ''}?`}
      description="Type the code the phone is showing. The phone gets access only if it matches — a wrong code is refused and logged."
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={approve.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            icon={<Check className="size-3.5" />}
            loading={approve.isPending}
          >
            Approve matching device
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} className="space-y-3">
        <Field
          label="Code shown on phone"
          htmlFor={inputId}
          error={error}
          hint={`${PAIRING_CODE_LENGTH} characters, on the phone's pairing screen. Typing it — rather than copying the one in the list — is the check.`}
        >
          <Input
            id={inputId}
            ref={codeInput}
            mono
            autoFocus
            value={code}
            invalid={!!error}
            maxLength={PAIRING_CODE_LENGTH}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            placeholder="e.g. 3F9A0C12BD"
            onChange={(e) => {
              setCode(normalizePairingCode(e.currentTarget.value));
              setServerError(null);
            }}
          />
        </Field>
      </form>
    </Dialog>
  );
}

/* ── Install ──────────────────────────────────────────────────────────────────
   The QR is a 15-minute download grant scoped to this console's own address
   (the API refuses anything but HTTPS or localhost). Installing the APK grants
   nothing by itself — the phone still enrolls and waits for approval above. */

function InstallPanel({
  canManage,
  installer,
  loading,
  error,
  onRetry,
}: {
  canManage: boolean;
  installer: FloorInstallerMetadata | null | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  const [link, setLink] = useState<FloorInstallerLink | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const now = useNow(!!link);
  const countdown = link ? installerCountdown(link.expiresAt, now) : null;

  const request = useMutation({
    mutationFn: () =>
      api.post<FloorInstallerLink>('/floor/installer-link', {
        origin: window.location.origin,
      }),
    onSuccess: (next) => {
      setLink(next);
      setLinkError(null);
    },
    onError: (err) => {
      // A standing condition (no installer staged, wrong origin), not an event
      // — so it stays on the panel rather than fading out of a toast.
      setLink(null);
      setLinkError(errorMessage(err));
    },
  });

  return (
    <Panel
      title="Install the floor app"
      description={
        // The metadata read is manager-only, so below that role its absence
        // says nothing about whether an installer is staged.
        !canManage
          ? 'A manager sees the staged installer here.'
          : installer
            ? `Available: v${installer.version} · ${installer.buildSha.slice(0, 8)}`
            : loading
              ? 'Checking the staged installer…'
              : 'No installer has been published.'
      }
      actions={
        canManage ? (
          <Button
            size="xs"
            icon={<QrCode className="size-3" />}
            loading={request.isPending}
            onClick={() => request.mutate()}
          >
            {link ? 'Show a new QR' : 'Show download QR'}
          </Button>
        ) : null
      }
    >
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-3 w-2/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ) : error ? (
        <ErrorState
          title="Installer status unavailable"
          message={errorMessage(error)}
          onRetry={onRetry}
        />
      ) : !canManage ? (
        <p className="text-ink-muted text-xs leading-relaxed">
          A manager generates the install QR. The link is scoped to this console&apos;s
          address and lasts 15 minutes; the phone then enrolls itself and waits in the
          list for approval.
        </p>
      ) : (
        <div className="space-y-3">
          {linkError ? (
            <Callout tone="negative" title="No install link">
              {linkError}
            </Callout>
          ) : null}

          {link && countdown ? (
            <div className="flex flex-col items-start gap-2">
              <img
                src={link.qrDataUrl}
                width={240}
                height={240}
                alt="Scan to download the Android floor app"
                className="border-line rounded border"
              />
              {countdown.expired ? (
                <span className="flex items-center gap-2 text-xs">
                  <Badge tone="negative">Link expired</Badge>
                  <span className="text-ink-muted">Show a new QR to try again.</span>
                </span>
              ) : (
                <span className="text-ink-muted text-xs">
                  Link expires in{' '}
                  <span className="text-ink font-mono tabular-nums">
                    {countdown.label}
                  </span>
                </span>
              )}
              {!countdown.expired ? (
                <a
                  href={link.url}
                  download
                  rel="external"
                  className="text-signal-ink text-xs underline-offset-2 hover:underline"
                >
                  Download Android app
                </a>
              ) : null}
            </div>
          ) : (
            <p className="text-ink-muted text-xs leading-relaxed">
              Scan the QR on the phone. The link is scoped to this console&apos;s address
              and expires 15 minutes after it is shown.
            </p>
          )}

          <p className="text-2xs text-ink-faint leading-relaxed">
            The phone may ask to allow installs from its browser. Installing grants
            nothing — approve the phone&apos;s pairing code in the list.
          </p>
        </div>
      )}
    </Panel>
  );
}

/** A ticking clock, only while something on screen is counting down. */
function useNow(active: boolean, intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [active, intervalMs]);
  return now;
}
